/**
 * Portfolio Management Module
 * ポートフォリオ管理 - 保有銘柄の追加・編集・削除・損益計算
 */

import { state, saveState } from '../state.js';
import { fetchStockData } from '../api/index.js';

// DOM Elements (cached on init)
let portfolioPanel, portfolioList, portfolioTotal, portfolioPL;
let addHoldingBtn;

/**
 * Initialize Portfolio Module
 */
export function initPortfolio() {
    portfolioPanel = document.getElementById('portfolio-panel');
    portfolioList = document.getElementById('portfolio-list');
    portfolioTotal = document.getElementById('portfolio-total');
    portfolioPL = document.getElementById('portfolio-pl');
    addHoldingBtn = document.getElementById('add-holding-btn');

    if (!portfolioPanel) return;

    // Initialize state if not exists
    if (!state.portfolio) {
        state.portfolio = [];
    }

    // Event Listeners
    if (addHoldingBtn) {
        addHoldingBtn.addEventListener('click', showAddHoldingModal);
    }

    // Initial render
    renderPortfolio();
}

/**
 * Add a new holding to portfolio
 */
export function addHolding(symbol, shares, avgCost, purchaseDate = new Date().toISOString()) {
    const holding = {
        id: Date.now().toString(),
        symbol: symbol.toUpperCase(),
        shares: parseFloat(shares),
        avgCost: parseFloat(avgCost),
        purchaseDate,
        currentPrice: null,
        marketValue: null,
        gainLoss: null,
        gainLossPercent: null
    };

    state.portfolio.push(holding);
    saveState();
    renderPortfolio();

    // Fetch current price
    updateHoldingPrice(holding.id);

    return holding;
}

/**
 * Remove holding from portfolio
 */
export function removeHolding(holdingId) {
    state.portfolio = state.portfolio.filter(h => h.id !== holdingId);
    saveState();
    renderPortfolio();
}

/**
 * Update holding details
 */
export function updateHolding(holdingId, updates) {
    const holding = state.portfolio.find(h => h.id === holdingId);
    if (holding) {
        Object.assign(holding, updates);
        saveState();
        renderPortfolio();
    }
}

/**
 * Fetch current price for a holding
 */
async function updateHoldingPrice(holdingId) {
    const holding = state.portfolio.find(h => h.id === holdingId);
    if (!holding) return;

    try {
        const data = await fetchStockData(holding.symbol, '1D');
        if (data && data.candleData && data.candleData.length > 0) {
            const latestCandle = data.candleData[data.candleData.length - 1];
            holding.currentPrice = latestCandle.close;
            holding.marketValue = holding.currentPrice * holding.shares;
            holding.gainLoss = (holding.currentPrice - holding.avgCost) * holding.shares;
            holding.gainLossPercent = ((holding.currentPrice - holding.avgCost) / holding.avgCost) * 100;

            saveState();
            renderPortfolio();
        }
    } catch (error) {
        console.warn(`Failed to fetch price for ${holding.symbol}:`, error.message);
    }
}

/**
 * Refresh all holdings prices
 */
export async function refreshPortfolioPrices() {
    for (const holding of state.portfolio) {
        await updateHoldingPrice(holding.id);
    }
}

/**
 * Calculate portfolio totals
 */
function calculatePortfolioTotals() {
    let totalValue = 0;
    let totalCost = 0;

    state.portfolio.forEach(h => {
        if (h.marketValue !== null) {
            totalValue += h.marketValue;
        }
        totalCost += h.avgCost * h.shares;
    });

    const totalPL = totalValue - totalCost;
    const totalPLPercent = totalCost > 0 ? ((totalValue - totalCost) / totalCost) * 100 : 0;

    return { totalValue, totalCost, totalPL, totalPLPercent };
}

/**
 * Render portfolio UI
 */
export function renderPortfolio() {
    if (!portfolioList) return;

    if (!state.portfolio || state.portfolio.length === 0) {
        portfolioList.innerHTML = '<p class="empty-state">No holdings added.</p>';
        if (portfolioTotal) portfolioTotal.textContent = '$0.00';
        if (portfolioPL) {
            portfolioPL.textContent = '$0.00';
            portfolioPL.className = 'value neutral';
        }
        return;
    }

    // Build holdings list
    const holdingsHTML = state.portfolio.map(h => {
        const plClass = h.gainLoss > 0 ? 'positive' : h.gainLoss < 0 ? 'negative' : 'neutral';
        const plSign = h.gainLoss >= 0 ? '+' : '';

        return `
            <div class="holding-card" data-id="${h.id}">
                <div class="holding-header">
                    <span class="holding-symbol">${h.symbol}</span>
                    <button class="holding-remove-btn" data-id="${h.id}" title="Remove">×</button>
                </div>
                <div class="holding-details">
                    <div class="detail-row">
                        <span>Shares</span>
                        <span>${h.shares.toLocaleString()}</span>
                    </div>
                    <div class="detail-row">
                        <span>Avg Cost</span>
                        <span>$${h.avgCost.toFixed(2)}</span>
                    </div>
                    <div class="detail-row">
                        <span>Current</span>
                        <span>${h.currentPrice !== null ? '$' + h.currentPrice.toFixed(2) : 'Loading...'}</span>
                    </div>
                    <div class="detail-row">
                        <span>Market Value</span>
                        <span>${h.marketValue !== null ? '$' + h.marketValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-'}</span>
                    </div>
                    <div class="detail-row ${plClass}">
                        <span>P/L</span>
                        <span>${h.gainLoss !== null ? plSign + '$' + h.gainLoss.toFixed(2) + ' (' + plSign + h.gainLossPercent.toFixed(2) + '%)' : '-'}</span>
                    </div>
                </div>
            </div>
        `;
    }).join('');

    portfolioList.innerHTML = holdingsHTML;

    // Add remove event listeners
    portfolioList.querySelectorAll('.holding-remove-btn').forEach(btn => {
        btn.addEventListener('click', (e) => {
            e.stopPropagation();
            const id = btn.dataset.id;
            if (confirm('Remove this holding?')) {
                removeHolding(id);
            }
        });
    });

    // Update totals
    const totals = calculatePortfolioTotals();

    if (portfolioTotal) {
        portfolioTotal.textContent = '$' + totals.totalValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }

    if (portfolioPL) {
        const sign = totals.totalPL >= 0 ? '+' : '';
        portfolioPL.textContent = `${sign}$${totals.totalPL.toFixed(2)} (${sign}${totals.totalPLPercent.toFixed(2)}%)`;
        portfolioPL.className = `value ${totals.totalPL > 0 ? 'positive' : totals.totalPL < 0 ? 'negative' : 'neutral'}`;
    }

    renderAnalytics();
}

/**
 * Render Portfolio Analytics (Donut Chart)
 */
function renderAnalytics() {
    const analyticsPanel = document.getElementById('portfolio-analytics');
    const donutChart = document.getElementById('allocation-donut');
    const donutLegend = document.getElementById('allocation-legend');

    if (!analyticsPanel || !donutChart || !donutLegend) return;

    if (!state.portfolio || state.portfolio.length === 0) {
        analyticsPanel.classList.add('hidden');
        return;
    }

    analyticsPanel.classList.remove('hidden');

    // Calculate weights
    let totalValue = 0;
    state.portfolio.forEach(h => {
        if (h.marketValue !== null) totalValue += h.marketValue;
    });

    if (totalValue === 0) return;

    // Sort by value desc
    const sortedHoldings = [...state.portfolio].sort((a, b) => (b.marketValue || 0) - (a.marketValue || 0));

    // Generate colors (simple palette)
    const colors = ['#238636', '#1f6feb', '#a371f7', '#f85149', '#d29922', '#8b949e'];

    let conicGradient = [];
    let currentAngle = 0;
    let legendHTML = '';

    sortedHoldings.forEach((h, index) => {
        const value = h.marketValue || 0;
        const percent = (value / totalValue) * 100;
        const color = colors[index % colors.length];

        // CSS Conic Gradient string construction
        // "color start% end%"
        conicGradient.push(`${color} 0 ${percent}%`);

        // Legend
        legendHTML += `
            <div class="legend-item">
                <span class="legend-color" style="background-color: ${color}"></span>
                <span class="legend-label">${h.symbol}</span>
                <span class="legend-value">${percent.toFixed(1)}%</span>
            </div>
        `;
    });

    // For conic-gradient, we actually need cumulative percentages or specific syntax
    // Simplest syntax: conic-gradient(red 0 20%, blue 0 60%, green 0 100%) - wait, second param is start position?
    // safer: red 0% 20%, blue 20% 60%, green 60% 100%

    let gradientParts = [];
    let start = 0;

    sortedHoldings.forEach((h, index) => {
        const value = h.marketValue || 0;
        const percent = (value / totalValue) * 100;
        const color = colors[index % colors.length];
        const end = start + percent;

        gradientParts.push(`${color} ${start}% ${end}%`);
        start = end;
    });

    donutChart.style.background = `conic-gradient(${gradientParts.join(', ')})`;
    donutLegend.innerHTML = legendHTML;
}

/**
 * Show Add Holding Modal
 */
function showAddHoldingModal() {
    // Create modal if not exists
    let modal = document.getElementById('holding-modal');
    if (!modal) {
        modal = document.createElement('div');
        modal.id = 'holding-modal';
        modal.className = 'modal-overlay';
        modal.innerHTML = `
            <div class="modal-content">
                <div class="modal-header">
                    <h3>Add Holding</h3>
                    <button class="modal-close-btn">×</button>
                </div>
                <form id="holding-form" class="modal-form">
                    <div class="form-group">
                        <label for="holding-symbol">Symbol</label>
                        <input type="text" id="holding-symbol" placeholder="AAPL" required maxlength="10">
                    </div>
                    <div class="form-group">
                        <label for="holding-shares">Shares</label>
                        <input type="number" id="holding-shares" placeholder="10" required min="0.001" step="0.001">
                    </div>
                    <div class="form-group">
                        <label for="holding-cost">Average Cost ($)</label>
                        <input type="number" id="holding-cost" placeholder="150.00" required min="0.01" step="0.01">
                    </div>
                    <div class="form-actions">
                        <button type="button" class="btn-secondary modal-cancel-btn">Cancel</button>
                        <button type="submit" class="btn-primary">Add</button>
                    </div>
                </form>
            </div>
        `;
        document.body.appendChild(modal);

        // Event Listeners
        modal.querySelector('.modal-close-btn').addEventListener('click', hideModal);
        modal.querySelector('.modal-cancel-btn').addEventListener('click', hideModal);
        modal.addEventListener('click', (e) => {
            if (e.target === modal) hideModal();
        });

        modal.querySelector('#holding-form').addEventListener('submit', (e) => {
            e.preventDefault();
            const symbol = document.getElementById('holding-symbol').value.trim();
            const shares = document.getElementById('holding-shares').value;
            const cost = document.getElementById('holding-cost').value;

            if (symbol && shares && cost) {
                addHolding(symbol, shares, cost);
                hideModal();
                e.target.reset();
            }
        });
    }

    modal.classList.add('active');
    document.getElementById('holding-symbol').focus();
}

function hideModal() {
    const modal = document.getElementById('holding-modal');
    if (modal) modal.classList.remove('active');
}
